// ===========================================
// The reply box (enquiries plan §5.5 "Replies", A3; saved templates, §12)
// ===========================================
// Emailed: the ball goes to the family and a chase date is set. Kept as a note: history only. A
// phone-only enquiry has nowhere to email, so it offers the note alone. "Insert M&G link" only
// when the school runs Meet & Greets. Shown only with enquiries.write (the panel gates it).
//
// Templates (Phase 7): "Templates" fills one in for THIS enquiry on the server (raw text: the reply
// is escaped once when sent), "Save as template" keeps the box's text as a new one (staff add, the
// owner manages them in Settings → Enquiries). A template whose booking link had to be left out
// (a live Meet & Greet, or no bookings) says so under the box, and while that note shows, Send by
// email asks first (Laura 26/10/2026).

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BookmarkAdd as SaveTemplateIcon,
  Handshake as HandshakeIcon,
  Notes as TemplatesIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { Enquiry, RenderedReplyTemplate } from '../../../api/enquiry.api';
import { REPLY_MAX, TEMPLATE_BODY_MAX, TEMPLATE_NAME_MAX } from '../../../api/enquiry.api';
import {
  apiErrorCode,
  apiErrorMessage,
  useAddReplyTemplate,
  useEnquiryBookingLink,
  useRenderReplyTemplate,
  useReplyTemplates,
  useReplyToEnquiry,
} from '../../../hooks/useEnquiries';
import { familyNamesIn, holdsLiveBooking, replaceSignedBookingLinks, swapFamilyNames, type FamilyNames } from './enquiryUi';
import ReplyTemplateBodyField from './ReplyTemplateBodyField';

interface EnquiryReplyBoxProps {
  enquiry: Enquiry;
  meetAndGreetEnabled: boolean;
}

export default function EnquiryReplyBox({ enquiry, meetAndGreetEnabled }: EnquiryReplyBoxProps) {
  const [message, setMessage] = useState('');
  const canEmail = !!enquiry.email;
  const [mode, setMode] = useState<'email' | 'note'>(canEmail ? 'email' : 'note');
  const reply = useReplyToEnquiry();
  const effectiveMode = canEmail ? mode : 'note';
  const { enqueueSnackbar } = useSnackbar();

  // What a template had to leave out; shown until dismissed or the reply goes.
  const [omitted, setOmitted] = useState<RenderedReplyTemplate['omitted']>([]);
  const [confirmSend, setConfirmSend] = useState(false);

  const send = () => {
    reply.mutate(
      { id: enquiry.id, message: message.trim(), sentViaEmail: effectiveMode === 'email' },
      {
        // A reply whose email failed keeps its text (and its note) so it can be sent again (review 5.7 M6).
        onSuccess: (result) => {
          if (result.emailSent !== false) {
            setMessage('');
            setOmitted([]);
          }
        },
      },
    );
  };

  // A template added to typed text can pass the limit (`maxLength` only stops typing): say so and
  // hold Send until it fits, rather than let the server refuse it (review L2).
  const overBy = message.length - REPLY_MAX;

  const handleSend = () => {
    if (!message.trim() || overBy > 0) return;
    // Laura 26/10/2026: a template's link was left out and the note still shows: ask before emailing.
    if (effectiveMode === 'email' && omitted.length > 0) {
      setConfirmSend(true);
      return;
    }
    send();
  };

  // The link is SIGNED per enquiry (`?e=`), so a booking made from it joins this enquiry; the server
  // makes it, never the browser. Hidden, with the reason, while a booking that cannot be replaced is
  // linked (the server's 409 says the same).
  const bookingLink = useEnquiryBookingLink();
  const liveBooking = holdsLiveBooking(enquiry.MeetAndGreet?.status);

  const insertMngLink = async () => {
    try {
      const { url } = await bookingLink.mutateAsync(enquiry.id);
      setMessage((prev) => prev + (prev ? '\n\n' : '') + `Book a Meet & Greet: ${url}`);
    } catch (error) {
      enqueueSnackbar(
        apiErrorCode(error) === 'ENQUIRY_ALREADY_LINKED'
          ? 'This enquiry already has a Meet & Greet booked, so there is no booking link to send.'
          : apiErrorMessage(error, 'Could not make the booking link'),
        { variant: 'error' },
      );
    }
  };

  // ---- Templates ----
  const templates = useReplyTemplates();
  const render = useRenderReplyTemplate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const templateList = Array.isArray(templates.data) ? templates.data : [];

  const applyTemplate = async (templateId: string) => {
    setMenuAnchor(null);
    try {
      const rendered = await render.mutateAsync({ enquiryId: enquiry.id, templateId });
      // Into an empty box as it is; after what is already typed, below it.
      setMessage((prev) => (prev.trim() ? `${prev}\n\n${rendered.text}` : rendered.text));
      // Notes ADD UP (review M1): a second template must not clear the first one's missing link.
      // Only the note's close button or a reply that goes clears them.
      setOmitted((prev) => [...prev, ...rendered.omitted.filter((o) => !prev.some((p) => p.field === o.field))]);
    } catch (error) {
      enqueueSnackbar(apiErrorMessage(error, 'Could not fill in the template'), { variant: 'error' });
    }
  };

  const [saveOpen, setSaveOpen] = useState(false);

  return (
    <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Reply
        </Typography>
        <Button
          size="small"
          startIcon={<TemplatesIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={render.isPending}
          aria-haspopup="menu"
          sx={{ textTransform: 'none' }}
        >
          Templates
        </Button>
        <Button
          size="small"
          startIcon={<SaveTemplateIcon />}
          onClick={() => setSaveOpen(true)}
          disabled={!message.trim()}
          sx={{ textTransform: 'none' }}
        >
          Save as template
        </Button>
      </Stack>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {templates.isLoading && <MenuItem disabled>Loading templates…</MenuItem>}
        {templates.isError && <MenuItem disabled>Could not load the templates</MenuItem>}
        {!templates.isLoading && !templates.isError && templateList.length === 0 && (
          <MenuItem disabled>No templates yet: write a reply and click “Save as template”</MenuItem>
        )}
        {templateList.map((t) => (
          <MenuItem key={t.id} onClick={() => void applyTemplate(t.id)}>
            <ListItemText primary={t.name} secondary={t.body.length > 60 ? `${t.body.slice(0, 60)}…` : t.body} />
          </MenuItem>
        ))}
      </Menu>

      {canEmail ? (
        <RadioGroup
          row
          value={mode}
          onChange={(e) => setMode(e.target.value === 'note' ? 'note' : 'email')}
          aria-label="How to record the reply"
        >
          <FormControlLabel value="email" control={<Radio size="small" />} label="Send by email" />
          <FormControlLabel value="note" control={<Radio size="small" />} label="Keep as a note" />
        </RadioGroup>
      ) : (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          No email address on this enquiry: the reply is kept as a note. Log a call with "+ Call".
        </Typography>
      )}
      <TextField
        multiline
        minRows={3}
        maxRows={6}
        fullWidth
        placeholder={effectiveMode === 'email' ? `Reply to ${enquiry.firstName}...` : 'Write a note...'}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        size="small"
        sx={{ mb: 1 }}
        inputProps={{ 'aria-label': 'Reply message', maxLength: REPLY_MAX }}
        error={overBy > 0}
        helperText={overBy > 0 ? `Too long to send: shorten it by ${overBy} characters (${message.length}/${REPLY_MAX})` : `${message.length}/${REPLY_MAX}`}
      />
      {omitted.length > 0 && (
        <Alert severity="warning" onClose={() => setOmitted([])} sx={{ mb: 1 }} data-testid="template-omitted-note">
          {omitted.map((o) => (
            <div key={o.field}>{o.message}</div>
          ))}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: meetAndGreetEnabled ? 'space-between' : 'flex-end', alignItems: 'center' }}>
        {meetAndGreetEnabled && !liveBooking && (
          <Tooltip title="Insert Meet & Greet booking link into reply">
            <span>
              <Button
                size="small"
                startIcon={<HandshakeIcon />}
                onClick={insertMngLink}
                disabled={bookingLink.isPending}
                sx={{ color: 'text.secondary', textTransform: 'none' }}
              >
                Insert M&G Link
              </Button>
            </span>
          </Tooltip>
        )}
        {meetAndGreetEnabled && liveBooking && (
          <Typography variant="caption" color="text.secondary" data-testid="mng-link-hidden-reason" sx={{ maxWidth: 260 }}>
            A Meet & Greet is already booked for this enquiry, so there is no booking link to send.
          </Typography>
        )}
        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSend} disabled={!message.trim() || overBy > 0 || reply.isPending}>
          {reply.isPending ? 'Saving...' : effectiveMode === 'email' ? 'Send Reply' : 'Save Note'}
        </Button>
      </Box>

      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send without the booking link?</DialogTitle>
        <DialogContent>
          {omitted.map((o) => (
            <DialogContentText key={o.field} sx={{ mb: 1 }}>
              {o.message}
            </DialogContentText>
          ))}
          <DialogContentText>The family will get the reply exactly as it is in the box.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Go back</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmSend(false);
              send();
            }}
          >
            Send anyway
          </Button>
        </DialogActions>
      </Dialog>

      {saveOpen && (
        <SaveTemplateDialog
          initialBody={message.trim()}
          family={{
            parentFirstName: enquiry.firstName,
            parentLastName: enquiry.lastName,
            childFirstNames: (Array.isArray(enquiry.Children) ? enquiry.Children : []).map((c) => c.firstName),
          }}
          onClose={() => setSaveOpen(false)}
        />
      )}
    </Box>
  );
}

/**
 * "Save as template": a name and the text, which starts as the box's. A template must fit EVERY
 * family (review M2, Andrew 26/10/2026): this enquiry's signed booking link is turned back into
 * `{{bookingLink}}` as the dialog opens (the server does the same on save), and this family's names
 * are spotted with one click to swap them for `{{parentName}}` / `{{childNames}}`. The server
 * refuses an unknown merge field (422) and the hook shows its reason.
 */
function SaveTemplateDialog({ initialBody, family, onClose }: { initialBody: string; family: FamilyNames; onClose: () => void }) {
  const [opened] = useState(() => {
    const unlinked = replaceSignedBookingLinks(initialBody);
    return { body: unlinked.text, linksSwapped: unlinked.replaced, cut: unlinked.text.length > TEMPLATE_BODY_MAX };
  });
  const [name, setName] = useState('');
  const [body, setBody] = useState(opened.body.slice(0, TEMPLATE_BODY_MAX));
  const add = useAddReplyTemplate();
  const namesFound = familyNamesIn(body, family);
  const swapped = swapFamilyNames(body, family);

  const save = async () => {
    try {
      await add.mutateAsync({ name: name.trim(), body: body.trim() });
      onClose();
    } catch {
      // The hook shows the server's reason (an unknown field, a name already in use).
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {opened.linksSwapped > 0 && (
            <Alert severity="info" data-testid="template-link-swapped">
              The booking link made for this family was turned back into {'{{bookingLink}}'}, so each family gets its own.
            </Alert>
          )}
          {opened.cut && (
            <Alert severity="warning">
              The reply is longer than a template can be ({TEMPLATE_BODY_MAX} characters), so the end was cut. Check it before saving.
            </Alert>
          )}
          {namesFound.length > 0 && (
            <Alert
              severity="warning"
              data-testid="template-family-names"
              action={
                swapped !== body ? (
                  <Button color="inherit" size="small" onClick={() => setBody(swapped)}>
                    Use merge fields
                  </Button>
                ) : undefined
              }
            >
              {swapped !== body
                ? `This text names this family (${namesFound.join(', ')}). Swap the names for merge fields so the template fits every family.`
                : `This text still mentions ${namesFound.join(', ')}. Change it before saving, so the template fits every family.`}
            </Alert>
          )}
          <TextField
            label="Template name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: TEMPLATE_NAME_MAX }}
            autoFocus
            fullWidth
            size="small"
          />
          <ReplyTemplateBodyField
            value={body}
            onChange={setBody}
            minRows={5}
            maxRows={12}
            helperText={`Use the fields below instead of names, so it fits every family. ${body.length}/${TEMPLATE_BODY_MAX}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={!name.trim() || !body.trim() || add.isPending}>
          Save template
        </Button>
      </DialogActions>
    </Dialog>
  );
}
